import { useFlipperFlag } from './useFlipperFlag'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { ScheduledPostTarget } from 'types'
import { AlmanacFileCalendarEvent } from 'screens/Calendar/types'

interface TargetAnalysisOldLogic {
  useOldLogic: true
  isPosted: boolean
  isFailed: boolean
}

interface TargetAnalysisNewLogic {
  useOldLogic: false
  isHybrid: boolean
  isPureSuccess: boolean
  isPureFailure: boolean
  hasAnySuccess: boolean
  hasAnyFailure: boolean
  successfulTargets: ScheduledPostTarget[]
  failedTargets: ScheduledPostTarget[]
  processingTargets: ScheduledPostTarget[]
  totalTargets: number
}

export type TargetAnalysisResult = TargetAnalysisOldLogic | TargetAnalysisNewLogic

export const useCalendarTargetAnalysis = (
  event: Pick<AlmanacFileCalendarEvent, 'status' | 'scheduled_post_targets'>,
): TargetAnalysisResult => {
  const targetStatusEnabled = useFlipperFlag('calendar_target_status')

  // Use old logic when feature flag disabled or no targets available
  if (!targetStatusEnabled || !event.scheduled_post_targets?.length) {
    return {
      useOldLogic: true,
      isPosted: event.status === PostStatusPropertyValue.Posted,
      isFailed: event.status === PostStatusPropertyValue.Failed,
    }
  }

  const targets = event.scheduled_post_targets
  const successfulTargets = targets.filter((target) => target.status === 'published')
  const failedTargets = targets.filter((target) => target.status === 'failed')
  const processingTargets = targets.filter((target) =>
    ['pending', 'queued', 'processing', 'retrying'].includes(target.status),
  )

  const hasAnySuccess = successfulTargets.length > 0
  const hasAnyFailure = failedTargets.length > 0
  const isHybrid = hasAnySuccess && hasAnyFailure
  const isPureSuccess = hasAnySuccess && !hasAnyFailure && processingTargets.length === 0
  const isPureFailure = hasAnyFailure && !hasAnySuccess && processingTargets.length === 0

  return {
    useOldLogic: false,
    isHybrid,
    isPureSuccess,
    isPureFailure,
    hasAnySuccess,
    hasAnyFailure,
    successfulTargets,
    failedTargets,
    processingTargets,
    totalTargets: targets.length,
  }
}
